import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { shopify, delhivery } from '../services/api';
import {
  Calendar, ChevronLeft, ChevronRight, Package, Truck,
  CreditCard, Search, Loader2, ChevronDown, ChevronUp, IndianRupee, Clock,
  MapPin, Copy, Download
} from 'lucide-react';

type DateMode = 'single' | '7d' | '15d' | '30d' | 'custom';

const Dispatch: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [liveTracking, setLiveTracking] = useState<Record<number, any>>({});
  const [trackingLoading, setTrackingLoading] = useState<Record<number, boolean>>({});
  const [dateMode, setDateMode] = useState<DateMode>('single');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [exporting, setExporting] = useState(false);
  const [dispatchStats, setDispatchStats] = useState<any>({});

  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;

  const getDateRange = (): { start: string; end: string } => {
    if (dateMode === 'single') return { start: selectedDate, end: selectedDate };
    if (dateMode === 'custom') return { start: customStart, end: customEnd };
    const days = dateMode === '7d' ? 7 : dateMode === '15d' ? 15 : 30;
    const d = new Date();
    d.setDate(d.getDate() - days + 1);
    return { start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, end: today };
  };

  const fetchLiveTracking = async (orderId: number, awb: string) => {
    if (!awb || awb === 'None' || liveTracking[orderId]) return;
    const cleanAwb = awb.split('|')[0].trim();
    setTrackingLoading(p => ({ ...p, [orderId]: true }));
    try {
      const res = await delhivery.trackLive(cleanAwb);
      setLiveTracking(p => ({ ...p, [orderId]: res.data }));
    } catch {
      setLiveTracking(p => ({ ...p, [orderId]: { error: true } }));
    }
    setTrackingLoading(p => ({ ...p, [orderId]: false }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('tg_token');
      const resp = await fetch('/api/dispatch/pending-pickup', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_pickup_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
    } catch (e) {
      alert('Export failed');
    }
    setExporting(false);
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('delivered')) return { bg: 'var(--accent-green-soft)', text: 'var(--accent-green)' };
    if (s.includes('transit') || s.includes('shipped') || s.includes('picked')) return { bg: 'var(--accent-blue-soft)', text: 'var(--accent-blue)' };
    if (s.includes('out for delivery')) return { bg: 'var(--accent-purple-soft)', text: 'var(--accent-purple)' };
    if (s.includes('rto') || s.includes('return') || s.includes('undeliver') || s.includes('failed')) return { bg: 'var(--accent-red-soft)', text: 'var(--accent-red)' };
    if (s.includes('manifested') || s.includes('pickup')) return { bg: 'var(--accent-amber-soft)', text: 'var(--accent-amber)' };
    return { bg: 'var(--bg-elevated)', text: 'var(--text-secondary)' };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      if (!start || !end) { setLoading(false); return; }

      // Fetch orders fulfilled on this date range
      const res = await shopify.getOrders({
        status: 'fulfilled',
        fulfilled_start: start,
        fulfilled_end: end,
        limit: 500,
      });
      const res2 = await shopify.getOrders({
        status: 'delivered',
        fulfilled_start: start,
        fulfilled_end: end,
        limit: 500,
      });

      const allRaw = [...(res.data.orders || []), ...(res2.data.orders || [])];
      const seenIds = new Set<number>();
      const allDeduped = allRaw.filter(o => { if (seenIds.has(o.id)) return false; seenIds.add(o.id); return true; });

      // Show ALL fulfilled orders (manifested + in transit + delivered)
      allDeduped.sort((a: any, b: any) => {
        const da = a.fulfilled_at || a.created_at;
        const db2 = b.fulfilled_at || b.created_at;
        return new Date(db2).getTime() - new Date(da).getTime();
      });
      setOrders(allDeduped);

      // Fetch dispatch stats from backend
      try {
        const statsDate = dateMode === 'single' ? selectedDate : today;
        const statsRes = await shopify.getDispatchStats(statsDate);
        setDispatchStats(statsRes.data);
      } catch {}

    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [selectedDate, dateMode, customStart, customEnd]);

  // Fetch dispatch stats separately
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statsDate = dateMode === 'single' ? selectedDate : new Date().toISOString().split('T')[0];
        const statsRes = await shopify.getDispatchStats(statsDate);
        setDispatchStats(statsRes.data || {});
      } catch (e) {
        console.error('Stats fetch failed:', e);
      }
    };
    fetchStats();
  }, [selectedDate, dateMode]);

  const shiftDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateShort = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const filtered = search
    ? orders.filter(o =>
        (o.order_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.tracking_status || '').toLowerCase().includes(search.toLowerCase())
      )
    : orders;

  const totalRevenue = filtered.reduce((sum: number, o: any) => sum + (o.total || 0), 0);
  const totalItems = filtered.reduce((sum: number, o: any) => sum + (o.quantity || 1), 0);
  const codCount = filtered.filter((o: any) => o.payment_method === 'cod').length;
  const prepaidCount = filtered.filter((o: any) => o.payment_method === 'prepaid').length;

  const { start: rangeStart, end: rangeEnd } = getDateRange();
  const rangeLabel = dateMode === 'single'
    ? (isToday ? 'Today' : formatDate(selectedDate))
    : dateMode === 'custom'
    ? `${formatDateShort(customStart)} — ${formatDateShort(customEnd)}`
    : `Last ${dateMode === '7d' ? '7 days' : dateMode === '15d' ? '15 days' : '30 days'}`;

  const presets: { key: DateMode; label: string }[] = [
    { key: 'single', label: 'Day' },
    { key: '7d', label: '7 Days' },
    { key: '15d', label: '15 Days' },
    { key: '30d', label: 'Month' },
    { key: 'custom', label: 'Custom' },
  ];

  return (
    <Layout title="Dispatch Tracking" subtitle="Fulfilled orders — what was dispatched and when">
      <div className="animate-in" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {presets.map(p => (
              <button key={p.key} onClick={() => setDateMode(p.key)} style={{
                padding: '7px 14px', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', border: `1px solid ${dateMode === p.key ? 'var(--accent-green)' : 'var(--border)'}`,
                background: dateMode === p.key ? 'var(--accent-green-soft)' : 'var(--bg-card)',
                color: dateMode === p.key ? 'var(--accent-green)' : 'var(--text-muted)',
                transition: 'all 0.2s ease',
              }}>{p.label}</button>
            ))}
          </div>

          {dateMode === 'single' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '4px 6px',
            }}>
              <button onClick={() => shiftDate(-1)} style={{
                background: 'var(--bg-elevated)', border: 'none', borderRadius: 6,
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--text-secondary)',
              }}><ChevronLeft size={16} /></button>
              <Calendar size={14} color="var(--accent-green)" style={{ marginLeft: 6, marginRight: 8 }} />
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} max={today}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }} />
              <button onClick={() => shiftDate(1)} disabled={isToday} style={{
                background: isToday ? 'transparent' : 'var(--bg-elevated)', border: 'none', borderRadius: 6,
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isToday ? 'default' : 'pointer', color: isToday ? 'var(--text-muted)' : 'var(--text-secondary)', opacity: isToday ? 0.3 : 1,
              }}><ChevronRight size={16} /></button>
              {!isToday && (
                <button onClick={() => setSelectedDate(today)} style={{
                  background: 'var(--accent-green-soft)', border: 'none', borderRadius: 6,
                  padding: '6px 12px', fontSize: 11.5, fontWeight: 600, color: 'var(--accent-green)', cursor: 'pointer', marginLeft: 4,
                }}>Today</button>
              )}
            </div>
          )}

          {dateMode === 'custom' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '6px 12px',
            }}>
              <Calendar size={14} color="var(--accent-green)" />
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} max={customEnd || today}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>to</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} min={customStart} max={today}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }} />
            </div>
          )}

          {dateMode !== 'single' && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 2 }}>
              Showing: <span style={{ color: 'var(--accent-green)', fontWeight: 600 }}>{rangeLabel}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={handleExportReport} disabled={exporting} style={{
            padding: '9px 16px', background: 'linear-gradient(135deg, var(--accent-amber), #d97706)',
            border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 12.5, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
            opacity: exporting ? 0.7 : 1,
          }}><Download size={14} /> {exporting ? 'Exporting...' : 'Export Report'}</button>
        </div>

        <div style={{ position: 'relative' }}>
          <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search order, customer..."
            style={{
              padding: '9px 12px 9px 34px', background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none',
              fontFamily: 'inherit', width: 220,
            }} />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="animate-in-1" style={{ marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        {[
          { label: 'Ready to Ship', value: dispatchStats.ready_to_ship || 0, sub: 'Manifested today', icon: <Package size={17} color="#f97316" />, color: '#f97316', bg: 'rgba(251,146,60,0.1)' },
          { label: 'Ready for Pickup', value: dispatchStats.ready_for_pickup || 0, sub: 'From yesterday', icon: <Truck size={17} color="var(--accent-purple)" />, color: 'var(--accent-purple)', bg: 'var(--accent-purple-soft)' },
          { label: 'In Transit', value: dispatchStats.in_transit || 0, sub: 'Picked up by courier', icon: <Truck size={17} color="var(--accent-blue)" />, color: 'var(--accent-blue)', bg: 'var(--accent-blue-soft)' },
          { label: 'Revenue', value: `₹${(dispatchStats.total_revenue || totalRevenue).toLocaleString('en-IN')}`, sub: 'Dispatch value', icon: <IndianRupee size={17} color="var(--accent-amber)" />, color: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)' },
          { label: 'COD / Prepaid', value: `${dispatchStats.cod_count || codCount} / ${dispatchStats.prepaid_count || prepaidCount}`, sub: 'Payment split', icon: <CreditCard size={17} color="var(--accent-green)" />, color: 'var(--accent-green)', bg: 'var(--accent-green-soft)' },
        ].map((k, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '18px 20px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: k.bg, filter: 'blur(30px)', opacity: 0.6 }} />
            <div style={{ position: 'relative' }}>
              <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>{k.icon}</div>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{k.label}</div>
              <div className="kpi-value" style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.5, marginBottom: 4 }}>{k.value}</div>
              <div style={{ fontSize: 11.5, color: k.color, fontWeight: 500 }}>{k.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Orders List */}
      <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 50, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} /><br />Loading dispatches...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 50, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No dispatches {dateMode === 'single' ? (isToday ? 'today yet' : `on ${formatDate(selectedDate)}`) : `for ${rangeLabel}`}
          </div>
        ) : (
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: '100px 1.2fr 0.8fr 100px 100px 120px 40px',
              padding: '12px 18px', borderBottom: '1px solid var(--border)', gap: 8,
            }}>
              {['Order', 'Customer', 'Products', 'Amount', 'Payment', 'Status', ''].map(h => (
                <div key={h} style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</div>
              ))}
            </div>

            {filtered.map((order: any) => {
              const isExpanded = expandedOrder === order.id;
              const lineItems = order.line_items || [];
              const trackingStatus = (order.tracking_status || '').split('|');
              const awb = trackingStatus[0]?.trim() || '';
              const delhiveryStatus = trackingStatus[1]?.trim() || '';
              const sc = getStatusColor(delhiveryStatus || order.status);

              return (
                <div key={order.id}>
                  <div
                    onClick={() => {
                      const newExpanded = isExpanded ? null : order.id;
                      setExpandedOrder(newExpanded);
                      if (newExpanded && awb) fetchLiveTracking(order.id, awb);
                    }}
                    style={{
                      display: 'grid', gridTemplateColumns: '100px 1.2fr 0.8fr 100px 100px 120px 40px',
                      padding: '14px 18px', borderBottom: '1px solid var(--border)', gap: 8,
                      cursor: 'pointer', alignItems: 'center', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-card-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-blue)' }}>{order.order_name}</div>
                      {dateMode !== 'single' && order.fulfilled_at && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{formatDateShort(order.fulfilled_at)}</div>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{order.customer_name || '—'}</div>
                      {order.fulfilled_at && (
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={10} /> {formatTime(order.fulfilled_at)}
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lineItems.length > 0 ? lineItems.map((li: any) => li.product_title).join(', ') : `${order.quantity || 1} item${(order.quantity || 1) > 1 ? 's' : ''}`}
                    </div>

                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>₹{(order.total || 0).toLocaleString('en-IN')}</div>

                    <div>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase',
                        background: order.payment_method === 'cod' ? 'var(--accent-amber-soft)' : 'var(--accent-blue-soft)',
                        color: order.payment_method === 'cod' ? 'var(--accent-amber)' : 'var(--accent-blue)',
                      }}>{order.payment_method}</span>
                    </div>

                    <div>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                        background: sc.bg, color: sc.text,
                      }}>{delhiveryStatus || order.status}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {isExpanded ? <ChevronUp size={14} color="var(--text-muted)" /> : <ChevronDown size={14} color="var(--text-muted)" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '14px 18px 14px 118px', background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Order Details</div>

                      {lineItems.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                          {lineItems.map((li: any, i: number) => (
                            <div key={i} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)',
                              padding: '10px 14px', border: '1px solid var(--border)',
                            }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{li.product_title}</div>
                                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                                  Size: <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{li.variant_title || '—'}</span> · Qty: {li.quantity}
                                </div>
                              </div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>₹{(li.price || 0).toLocaleString('en-IN')}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      <div style={{ display: 'flex', gap: 20, fontSize: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                        <div><span style={{ color: 'var(--text-muted)' }}>Order Date:</span> <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{formatDate(order.created_at)}</span></div>
                        {order.fulfilled_at && <div><span style={{ color: 'var(--text-muted)' }}>Dispatched:</span> <span style={{ color: 'var(--accent-green)', fontWeight: 500 }}>{formatDate(order.fulfilled_at)} {formatTime(order.fulfilled_at)}</span></div>}
                        {awb && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ color: 'var(--text-muted)' }}>AWB:</span>
                            <span style={{ color: 'var(--accent-purple)', fontWeight: 600, fontFamily: 'monospace' }}>{awb}</span>
                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(awb); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1 }}>
                              <Copy size={10} color="var(--text-muted)" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Live Tracking */}
                      {awb && (() => {
                        const lt = liveTracking[order.id];
                        const ltLoading = trackingLoading[order.id];
                        if (ltLoading) return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Fetching live tracking...
                          </div>
                        );
                        if (!lt || lt.error) return (
                          <div style={{ padding: '10px 14px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
                            {lt?.error ? 'Could not fetch tracking.' : 'Click to load tracking.'}
                            <button onClick={(e) => { e.stopPropagation(); fetchLiveTracking(order.id, awb); }}
                              style={{ marginLeft: 8, background: 'var(--accent-blue-soft)', border: 'none', borderRadius: 4, padding: '3px 10px', color: 'var(--accent-blue)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Retry</button>
                          </div>
                        );
                        const sc2 = getStatusColor(lt.status_raw || '');
                        return (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Live Tracking</div>
                            <div style={{ display: 'flex', gap: 16, padding: '12px 16px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>STATUS</div>
                                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: sc2.bg, color: sc2.text }}>{lt.status_raw}</span>
                              </div>
                              {lt.edd && <div><div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>EST. DELIVERY</div><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-amber)' }}>{lt.edd}</span></div>}
                              {lt.destination && <div><div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>DESTINATION</div><span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lt.destination}</span></div>}
                            </div>
                            {lt.activities && lt.activities.length > 0 && (
                              <div style={{ maxHeight: 200, overflowY: 'auto', paddingRight: 4 }}>
                                {lt.activities.slice(0, 8).map((act: any, i: number) => (
                                  <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? 'var(--accent-green)' : 'var(--border-light)' }} />
                                      {i < Math.min(lt.activities.length, 8) - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)', minHeight: 16 }} />}
                                    </div>
                                    <div style={{ flex: 1, paddingBottom: 4 }}>
                                      <div style={{ fontSize: 12, fontWeight: 500, color: i === 0 ? 'var(--accent-green)' : 'var(--text-primary)' }}>{act.status || act.activity}</div>
                                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 1 }}>
                                        {act.location && <><MapPin size={9} style={{ display: 'inline', verticalAlign: 'middle' }} /> {act.location} · </>}{act.date}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default Dispatch;
